import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Copy, KeyRound, LogIn, Trash2, UserPlus } from "lucide-react";
import { createTestUser, deleteTestUser, generateRecoveryLink } from "@/lib/admin-test-user.functions";

export const Route = createFileRoute("/admin/test-ucet")({
  component: TestAccountPage,
  head: () => ({
    meta: [
      { title: "Testovací účet — Cerveza.International" },
      { name: "description", content: "Rychlé vytvoření testovacího účtu a ověření přihlášení." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

type Created = { userId: string; email: string; password: string; fullName: string };

function TestAccountPage() {
  const createFn = useServerFn(createTestUser);
  const deleteFn = useServerFn(deleteTestUser);
  const recoveryFn = useServerFn(generateRecoveryLink);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [referrerId, setReferrerId] = useState("");
  const [created, setCreated] = useState<Created | null>(null);
  const [signingIn, setSigningIn] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [recoveryLink, setRecoveryLink] = useState("");

  const create = useMutation({
    mutationFn: () =>
      createFn({
        data: {
          ...(email.trim() ? { email: email.trim() } : {}),
          ...(password.trim() ? { password: password.trim() } : {}),
          ...(fullName.trim() ? { fullName: fullName.trim() } : {}),
          ...(referrerId.trim() ? { referrerId: referrerId.trim() } : {}),
        },
      }),
    onSuccess: (res) => {
      setCreated({ userId: res.userId, email: res.email, password: res.password, fullName: res.fullName });
      setRecoveryEmail(res.email);
      toast.success("Testovací účet je připraven.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: (userId: string) => deleteFn({ data: { userId } }),
    onSuccess: () => {
      toast.success("Testovací účet byl smazán.");
      setCreated(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const recovery = useMutation({
    mutationFn: () =>
      recoveryFn({
        data: {
          email: recoveryEmail.trim(),
          redirectTo: window.location.origin + "/auth/reset-password",
        },
      }),
    onSuccess: (res) => {
      setRecoveryLink(res.actionLink);
      toast.success("Odkaz pro reset hesla byl vygenerován.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const copy = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} zkopírováno.`);
    } catch {
      toast.error("Kopírování se nezdařilo.");
    }
  };

  /** Odhlásí admina a přihlásí testovací účet, pak otevře /moje-podniky. */
  const signInAsTest = async () => {
    if (!created) return;
    setSigningIn(true);
    try {
      await supabase.auth.signOut();
      const { error } = await supabase.auth.signInWithPassword({
        email: created.email,
        password: created.password,
      });
      if (error) throw new Error(error.message);
      window.location.assign("/moje-podniky");
    } catch (e) {
      setSigningIn(false);
      toast.error(e instanceof Error ? e.message : "Přihlášení se nezdařilo.");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-2xl">Testovací účet</h1>
        <p className="text-sm text-muted-foreground">
          Vytvoří okamžitě funkční účet s potvrzeným e-mailem a známým heslem — můžete se rovnou
          přihlásit a ověřit, že /moje-podniky reálně funguje.
        </p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <UserPlus className="h-4 w-4" /> Vytvořit účet
          </CardTitle>
          <CardDescription>Prázdná pole se doplní automaticky.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>E-mail</Label>
              <Input
                placeholder="test+xyz@cerveza.international"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Heslo</Label>
              <Input
                placeholder="vygeneruje se automaticky"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Jméno</Label>
              <Input value={fullName} onChange={(e) => setFullName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Doporučitel (UUID, volitelné)</Label>
              <Input
                className="font-mono text-xs"
                value={referrerId}
                onChange={(e) => setReferrerId(e.target.value)}
              />
            </div>
          </div>
          <Button onClick={() => create.mutate()} disabled={create.isPending}>
            {create.isPending ? "Vytvářím…" : "Vytvořit testovací účet"}
          </Button>
        </CardContent>
      </Card>

      {created && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Přihlašovací údaje</CardTitle>
            <CardDescription>
              Uložte si je — heslo už znovu nezobrazíme po opuštění stránky.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {[
              { label: "E-mail", value: created.email },
              { label: "Heslo", value: created.password },
              { label: "ID uživatele", value: created.userId },
            ].map((row) => (
              <div key={row.label} className="flex items-center gap-2">
                <span className="w-32 text-sm text-muted-foreground">{row.label}</span>
                <code className="flex-1 truncate rounded bg-muted px-2 py-1 font-mono text-xs">
                  {row.value}
                </code>
                <Button size="sm" variant="outline" onClick={() => copy(row.value, row.label)}>
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
            <div className="flex flex-wrap gap-2 pt-2">
              <Button onClick={signInAsTest} disabled={signingIn}>
                <LogIn className="mr-2 h-4 w-4" />
                {signingIn ? "Přihlašuji…" : "Přihlásit se a otevřít /moje-podniky"}
              </Button>
              <Button
                variant="outline"
                onClick={() => remove.mutate(created.userId)}
                disabled={remove.isPending}
              >
                <Trash2 className="mr-2 h-4 w-4" /> Smazat účet
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Pozor: přihlášením jako testovací účet se odhlásíte ze svého admin účtu.
            </p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <KeyRound className="h-4 w-4" /> Test resetu hesla
          </CardTitle>
          <CardDescription>
            Vygeneruje stejný recovery odkaz, jaký dostane uživatel e-mailem. Otevřením ověříte,
            že se nové heslo uloží a funguje pro příští přihlášení.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1.5">
            <Label>E-mail účtu</Label>
            <Input value={recoveryEmail} onChange={(e) => setRecoveryEmail(e.target.value)} />
          </div>
          <Button
            variant="outline"
            onClick={() => recovery.mutate()}
            disabled={recovery.isPending || !recoveryEmail.trim()}
          >
            {recovery.isPending ? "Generuji…" : "Vygenerovat odkaz pro reset"}
          </Button>
          {recoveryLink && (
            <div className="space-y-2 rounded-md border p-3">
              <code className="block break-all rounded bg-muted px-2 py-1 font-mono text-xs">
                {recoveryLink}
              </code>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => copy(recoveryLink, "Odkaz")}>
                  <Copy className="mr-2 h-3.5 w-3.5" /> Kopírovat odkaz
                </Button>
                <Button size="sm" asChild>
                  <a href={recoveryLink}>Otevřít reset hesla</a>
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Odkaz je jednorázový — otevřením se aktivuje recovery relace a nastavíte nové heslo.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
